import * as d3 from 'd3';

import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import {Sequence} from 'components/Sequence';
import LayeredView, {LayeredViewOptions} from 'molbio/maps/linear/LayeredView';
import {Translations} from 'molbio/maps/linear/LinearLayout';
import {
  DisplayAminoAcid,
  getDisplayAminoAcids,
  getTranslationAADisplayProperties,
} from 'molbio/maps/linear/TranslationDisplayUtil';
import {AlignmentMapState, SequenceLinearMapState} from 'molbio/maps/MapStates';
import {Selection} from 'molbio/maps/selectables/Selection';
import {AminoAcid} from 'molbio/MolBioConstants';
import BaseTranslation from 'molbio/translations/backbone/BaseTranslation';
import Translation from 'molbio/translations/backbone/Translation';
import {TabState} from 'Navigation';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';

import {Layout} from './LinearLayout';

type TranslationsViewModel = SequenceLinearMapState | AlignmentMapState;

export interface TranslationsViewOptions extends LayeredViewOptions {
  model: TranslationsViewModel;
}

// Renders a series of arrows with amino acids to represent a translation of bases to AAs
abstract class TranslationLayeredView<TranslationModel extends BaseTranslation> extends LayeredView<
  Sequence,
  TranslationModel
> {
  layout!: Layout;
  setting!: string;
  tabstate!: TabState;
  opacityFactor!: number;
  fontOpacityFactor!: number;
  translationLayout!: Translations;
  enableMousedown!: boolean;
  model!: TranslationsViewModel;

  initialize(options: TranslationsViewOptions): void {
    super.initialize(options);
    this.tabstate = options.tabstate;
    // @ts-expect-error: TS cannot handle multiple interfaces with generics
    this.translationLayout = this.model.get('layout').Translations;
    this.opacityFactor = 1;
    this.fontOpacityFactor = 1;
    this.enableMousedown = true;
  }

  indexShown(): boolean {
    return false;
  }

  makeLayerPiece(enter: d3.Selection<SVGElement, TranslationModel, SVGElement, unknown>): void {
    enter.each(function (): JQuery {
      return $(this).tooltip({
        delay: 100,
        trigger: 'hover',
        placement: 'top',
        container: 'body',
      });
    });
  }

  getRenderStart(): number {
    // Override in subclass and listen to range
    // To only render what's on screen
    return this.slice.start();
  }

  getRenderEnd(): number {
    // Override in subclass and listen to range
    // To only render what's on screen
    return this.slice.end();
  }

  aminoAcidFillColor(aa: AminoAcid, translation: TranslationModel): string {
    return getTranslationAADisplayProperties(
      aa,
      translation.selected() && this.enableMousedown,
      this.opacityFactor
    ).aaFillColor;
  }

  aminoAcidGlyphColor(aa: AminoAcid): string {
    return getTranslationAADisplayProperties(aa, this.enableMousedown, this.opacityFactor).aaGlyphColor;
  }

  fontWeight(aa: AminoAcid, translation: TranslationModel): number {
    return getTranslationAADisplayProperties(
      aa,
      translation.selected() && this.enableMousedown,
      this.opacityFactor
    ).fontWeight;
  }

  fillOpacity(aa: AminoAcid, translation: TranslationModel): number {
    return getTranslationAADisplayProperties(
      aa,
      translation.selected() && this.enableMousedown,
      this.opacityFactor
    ).fillOpacity;
  }

  _clickHandler(
    e: Event,
    selection: Selection,
    translation: TranslationModel,
    start: number,
    end: number
  ): void {
    const translations = this.model.sequence<Sequence | GapSequenceWrapper>().translations();

    if (this.enableMousedown) {
      if ((e as MouseEvent).shiftKey) {
        selection.expandRange(start, end);
        // expandRange will trigger a linearExtend or circularExtend event which removes
        // the translation selection so re-select it afterwards.
        translation.set({selected: true});
      } else {
        selection.reset(start);
        selection.setEndpoints(start, end);
        translations.each((t: Translation) => t.set({selected: t.uid() === translation.uid()}));
        selection.addHandlers({
          reset() {
            return translation.set({selected: false});
          },
          linearExtend() {
            return translation.set({selected: false});
          },
          circularExtend() {
            return translation.set({selected: false});
          },
        });
      }
      e.stopPropagation();
    }
  }

  _dblClickHandler(e: Event, selection: Selection, translation: TranslationModel): void {
    if (this.enableMousedown) {
      const start = translation.start();
      const end = translation.end();
      selection.reset(start);
      // reset removes the translation selection so re-select it afterwards.
      translation.set({selected: true});
      selection.setEndpoints(start, end);
      selection.addHandlers({
        reset() {
          return translation.set({selected: false});
        },
        linearExtend() {
          return translation.set({selected: false});
        },
        circularExtend() {
          return translation.set({selected: false});
        },
      });
      e.stopPropagation();
    }
  }

  // renders a single translation
  rerenderSingle(
    el: Element,
    start: number,
    end: number,
    dx: number,
    dy: number,
    translation: TranslationModel,
    xx: number,
    yy: number
  ): void {
    let aminoAcidRawData: Array<DisplayAminoAcid>;
    let height = dy;
    const fontType = this.translationLayout.get('single').get('type');
    const colorScheme = this.translationLayout.get('colorScheme');

    const bpWidth = fontType._width + fontType.letterSpacing;

    const sliceStart = this.slice.start();
    const sliceEnd = this.slice.end();
    const sequence = this.model.sequence<Sequence | GapSequenceWrapper>();
    if (this.translationLayout.get('visible')) {
      aminoAcidRawData = translation.getAminoAcids(
        sequence,
        Math.max(start, this.getRenderStart()),
        Math.min(end, this.getRenderEnd()),
        colorScheme
      ) as Array<DisplayAminoAcid>;
    } else {
      aminoAcidRawData = [];
    }

    // get data for rendering each amino acid
    const data = getDisplayAminoAcids(aminoAcidRawData, sliceStart, sliceEnd, sequence.length());

    // render amino acids
    d3.select(el)
      .attr('transform', `translate(0, ${yy})`)
      .attr('data-test-component', 'translationLayeredView')
      .attr('data-test-element', 'root');

    const x = this.slice.x();

    const root = d3
      .select(el)
      .selectAll('g.amino-acid-group')
      .data(data)
      .join((enter) => {
        const aminoAcidGroup = enter.append('svg:g').classed('amino-acid-group', true);
        aminoAcidGroup.append('svg:path');

        const aminoAcidGlyphGroup = aminoAcidGroup.append('svg:g').classed('amino-acid-glyph-group', true);
        aminoAcidGlyphGroup.append('svg:rect').classed('amino-acid-glyph-container', true);
        aminoAcidGlyphGroup.append('svg:text').classed('amino-acid', true);
        return aminoAcidGroup;
      })
      .attr('transform', (aa) => `translate(${x(aa.startPos)}, 0)`);

    const sel = sequence.selection();

    const getBaseWidth = (aa: DisplayAminoAcid): number => aa.bp * bpWidth;
    const arrowWidth = bpWidth / 2;
    root
      .select('path')
      .attr('d', (aa) => {
        const baseWidth = getBaseWidth(aa);
        const width = baseWidth + arrowWidth;
        height = fontType.height();
        return `M 0 0 L ${baseWidth} 0 L ${width} ${height / 2} L ${baseWidth} ${height} \
L 0 ${height} L ${width - baseWidth} ${height / 2} L 0 0`;
      })
      .attr('shape-rendering', 'geometricPrecision')
      .attr('transform', (aa) => {
        const baseWidth = getBaseWidth(aa);
        if (translation.strand() === -1) {
          return `rotate(180 ${baseWidth / 2} ${height / 2})`;
        } else {
          return '';
        }
      })
      .attr('stroke', () => this.strokeColor())
      .attr('fill', (aa: DisplayAminoAcid) => this.aminoAcidFillColor(aa, translation))
      .attr('fill-opacity', (aa: DisplayAminoAcid) => this.fillOpacity(aa, translation))
      .classed(`translation-${translation.uid()}`, true)
      .on('mousedown', (e, aa: AminoAcid) => {
        this._clickHandler(e, sel, translation, aa.indexStart, aa.indexEnd);
      })
      .on('dblclick', (e) => {
        this._dblClickHandler(e, sel, translation);
      });

    root
      .select('.amino-acid-glyph-container')
      .attr('x', 0)
      .attr('y', height)
      .attr('width', (aa) => {
        const baseWidth = getBaseWidth(aa);
        return baseWidth + arrowWidth;
      })
      .attr('height', this.indexShown() ? height : 0)
      .attr('fill', 'none')
      .attr('stroke', 'none');

    root
      .select('.amino-acid')
      .text((aa) => {
        if (aa.bp > 1) {
          return aa.b;
        }
        return '';
      })
      .attr('fill', (aa: DisplayAminoAcid) => this.aminoAcidGlyphColor(aa))
      .attr('data-test-component', 'translationLayeredView')
      .attr('data-test-element', 'aminoAcidResidue')
      .attr('font-size', fontType.fontSize)
      .attr('font-weight', (aa: DisplayAminoAcid) => this.fontWeight(aa, translation))
      .attr('fill-opacity', this.fontOpacityFactor)
      .attr('x', (aa) => {
        if (aa.bp >= 3) {
          return (aa.bp * bpWidth - fontType._width) / 2;
        } else if (aa.bp === 2) {
          return bpWidth + fontType.letterSpacing;
        } else {
          return 0;
        }
      })
      .attr('y', this.indexShown() ? height + fontType.fontSize / 2 : fontType.fontSize / 2)
      .attr('pointer-events', 'none')
      .attr('dominant-baseline', 'central')
      .classed(`aminoAcid-${translation.uid()}`, true);

    addTestAttrsToDOMElement(this.el, {component: 'SliceView', element: 'translations'});
  }

  strokeColor(): string | null {
    return null;
  }
}

export default TranslationLayeredView;
